import React, { useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import configment from '@adc/configment'
import instrument from '@adc/instrument'
import analytics from '@adc/analytics'
import logger from '@adc/logger'
import {
  hooks,
  ApplicationNotificationsProvider,
  ApplicationThemeProvider,
  ProductLine,
  useApplicationTheme,
} from '@adc/ui-core'
import { ExperimentFeatureManagementProvider } from '@adc/experiments'
import Notification from '../notification/index.jsx'
import ApplicationHeader from './applicationHeader'
import { ApplicationContextProvider } from './context'
import ApplicationErrorBoundary from './errorBoundary'
import { ApplicationModalsProvider } from './modals'
import SharedPageMetadata from './sharedPageMetadata'
import Footer from '../footer'
import { ApplicationQueuesProvider } from './queues'
import { ApplicationSurveyProvider } from './applicationSurvey'
import classNames from 'classnames/bind'
import cls from './shell.scss'
import SurveyButton from '../surveyButton/index.jsx'
import { enums } from '@adc/sdk'
import useFreshnessDetection from '../../hooks/useFreshnessDetection'

const cx = classNames.bind(cls)

// LD forces us to use strings, so when we don't want a maintenance banner we need to set the string to a magic value
const MAINTENANCE_BANNER_DISABLED_STRING = 'false'

//
// NOTES:
// - Need to design a mutually exclusive data structure so that the application always knows which modal is open at any
//   point
// - Need to design a `FloatingMessage` notifier that serves the purpose of `resi-search` and other apps use the
//   `Message` component for.
// - We need to solve the Helmet issue not being able to store favicons from inside here because it's stored as a data
//   uri. We may need to add a special case here to our policy of libraries being self-building.
// - Add in capabilities which would define shared behaviors all applications inherit, while providing a simple way for
//  applications to pass more details that the shared details should take into account.
//  ```
// capabilities: {
//   authentication: {},
//   criticalAreaVerification: {},
//   accessibility: {
//     equalweb: {}
//   }
// },
//  ```
//
// TODO:
// - We could track page loads using @adc/analytics in one place here so the apps don't have to deal with that, and we
//   can ensure everything is tracked consistently.
// - Could we use this to inject global styles so the app does not have to remember to import them for the app to render
//   correctly if they use this shell?.
export const DEFAULT_CAPABILITIES = Object.freeze({
  notifications: {},
  accessibility: {
    equalweb: { site_key: '', enabled: false },
  },
})

const DEFAULT_THEME = {
  productLine: ProductLine.AuctionCom,
  version: 1,
}

const AuctionApplicationShell = ({
  children,
  sspConfig = {},
  user = {},
  auth,
  headerProps,
  footerProps,
  helmetConfig = {},
  featureFlags = {},
  overridableComponents,
  capabilities = {},
  experiments = {},
  embeddedContext,
  cls = {},
  theme: themeInjected,
  viewingFrom = enums.CurrentTargetAudience.Buyer,
  history,
}) => {
  useFreshnessDetection()

  // This is a shared well-known trigger for our integration tests to wait before testing. And also adds an attribute to
  // indicate which app is currently serving the page. Which may seem trivial, but apart from allowing simple assertions
  // that we are on the correct app, it also helps in cases where we are restructuring and extracting things from one
  // app to another, and are rolling out the traffic between the two.
  hooks.useMountEffect(() => {
    instrument.initialize()
    document.body.setAttribute('data-app-state', 'ready')
    document.body.setAttribute('data-app-id', sspConfig.APP_NAME)
    const { pageTracking } = capabilities
    if (pageTracking?.enabled && pageTracking.options?.domainEvents) {
      const productLine = configment.get('APP_THEME', ProductLine.AuctionCom)
      analytics.trackInstrumentPageLoad(user, productLine)
      setTimeout(() => {
        // we delay the setting of the listener as to avoid possible duplicate initial page load tracking
        // (this is possible if there are nested routers, such as in Seller flow section)
        history?.listen(listener => {
          // this will listen for the react router url change
          analytics.trackInstrumentPageLoad(user, productLine)
        })
      })
    }
  })

  /**
   * There's a lot going on here to handle different shell usages:
   * - themeInjected: For passing theme in directly via theme prop
   * - themeFromContext: For using theme from context... this may seem weird as we set theme context in here as well,
   *     but this is to accommodate cases where the shell is used in a context where the theme is already set.
   * - themeFromConfigE: For using theme from sspConfig (passed in) or configment directly
   */
  const themeFromContext = useApplicationTheme()
  const theme = useMemo(() => {
    let newTheme = DEFAULT_THEME

    if (themeInjected) {
      newTheme = themeInjected
    } else if (themeFromContext) {
      newTheme = themeFromContext
    } else {
      const themeFromConfig =
        sspConfig?.APP_THEME ||
        configment.get('APP_THEME', ProductLine.AuctionCom)

      if (themeFromConfig) {
        if (Object.values(ProductLine).includes(themeFromConfig)) {
          newTheme = {
            productLine: themeFromConfig,
            version: 1,
          }
        }
      }
    }
    logger.info('Theme:', newTheme.productLine)
    return newTheme
  }, [sspConfig?.APP_THEME, themeFromContext, themeInjected])

  // used to hand off element where we want to render page banner
  const pageBannerContainerRef = useRef(null)

  const notification = useMemo(() => {
    const bannerMessage = featureFlags['fe.resi.maintenance-banner']

    return bannerMessage && bannerMessage !== MAINTENANCE_BANNER_DISABLED_STRING
      ? {
          text: (
            <div
              style={{ maxHeight: 60, overflowY: 'auto' }}
              dangerouslySetInnerHTML={{
                __html: bannerMessage,
              }}
            />
          ),
        }
      : null
  }, [featureFlags])

  // Yes, this is weird. No, it's not ideal. We do it here because the construction of headerProps across the apps is
  // not consistent, and instead of always keeping n apps up to do exactly we choose to set a reasonable default when it
  // comes to props controlled by feature flags that this this library owns. This also allows us to disable conditional
  // flag behavior in one central place for functionality that is wholly owned within the scope of this library.
  const finalHeaderProps = useMemo(
    () => ({
      showPurchaseProfileTooltip:
        featureFlags['fe.resi.enable_purchase_profile_nudge'],
      ...headerProps,
    }),
    [featureFlags, headerProps]
  )

  const productLineFromHost = hooks.useProductLineFromHost()

  const memoizedCapabilities = useMemo(() => {
    /**
     * Get accessibility options from configment per product line e.g smartsale, auctioncom.
     * in format of { equalweb: { site_key: 'site key', enabled: true } }
     */
    const accessibility =
      configment.get('accessibility', {})[productLineFromHost] || {}

    return _.merge(
      {},
      DEFAULT_CAPABILITIES,
      {
        accessibility,
        authentication: {
          enabled: false,
          options: {},
        },
        surveyMonkey: {
          enabled: featureFlags['fe.resi.enable_feedback_button'],
        },
      },
      capabilities
    )
  }, [capabilities, featureFlags, productLineFromHost])

  const memoizedPageBannerRef = useMemo(
    () => capabilities.notifications?.pageBannerRef || pageBannerContainerRef,
    [capabilities.notifications, pageBannerContainerRef]
  )
  return (
    <ApplicationContextProvider
      sspConfig={sspConfig}
      user={user}
      auth={auth}
      featureFlags={featureFlags}
      capabilities={memoizedCapabilities}
      experiments={experiments}
      embeddedContext={embeddedContext}
    >
      <ApplicationThemeProvider theme={theme}>
        <ApplicationErrorBoundary user={user}>
          <React.StrictMode>
            <ApplicationQueuesProvider>
              <ExperimentFeatureManagementProvider
                user={experiments.statsigUser}
                values={experiments.initializeResponse}
              >
                <ApplicationSurveyProvider>
                  <ApplicationNotificationsProvider
                    pageBannerContainerRef={memoizedPageBannerRef}
                  >
                    <ApplicationModalsProvider>
                      <SharedPageMetadata
                        helmetConfig={helmetConfig}
                        sspConfig={sspConfig}
                        featureFlags={featureFlags}
                        accessibilityOptions={
                          memoizedCapabilities.accessibility
                        }
                      />
                      <SurveyButton
                        isCapabilityEnabled={
                          memoizedCapabilities.surveyMonkey?.enabled
                        }
                        viewingFrom={viewingFrom}
                      />
                      {notification && (
                        <Notification
                          notification={notification}
                          elmId='sticky_header_notification'
                        />
                      )}
                      <div className={cx('container', cls.container)}>
                        <div className={cx('header', cls.header)}>
                          {overridableComponents.header ?? (
                            <ApplicationHeader
                              headerProps={finalHeaderProps}
                              featureFlags={featureFlags}
                            />
                          )}
                        </div>
                        <div ref={pageBannerContainerRef} />
                        <div className={cx('main-content', cls.content)}>
                          {children}
                        </div>
                        <div className={cx('footer', cls.footer)}>
                          {overridableComponents.footer ?? (
                            <Footer {...footerProps} />
                          )}
                        </div>
                      </div>
                    </ApplicationModalsProvider>
                  </ApplicationNotificationsProvider>
                </ApplicationSurveyProvider>
              </ExperimentFeatureManagementProvider>
            </ApplicationQueuesProvider>
          </React.StrictMode>
        </ApplicationErrorBoundary>
      </ApplicationThemeProvider>
    </ApplicationContextProvider>
  )
}

AuctionApplicationShell.propTypes = {
  auth: PropTypes.object,
  user: PropTypes.object,
  sspConfig: PropTypes.object,
  capabilities: PropTypes.shape({
    notifications: PropTypes.shape({
      pageBannerRef: PropTypes.object,
    }),
    accessibility: PropTypes.shape({
      equalweb: PropTypes.object,
    }),
  }),
  headerProps: PropTypes.object,
  footerProps: PropTypes.object,
  helmetConfig: PropTypes.object,
  featureFlags: PropTypes.object,
  overridableComponents: PropTypes.object,
  experiments: PropTypes.object,
  embeddedContext: PropTypes.string,
  children: PropTypes.node.isRequired,
  cls: PropTypes.shape({
    container: PropTypes.string,
    header: PropTypes.string,
    content: PropTypes.string,
    footer: PropTypes.string,
  }),
  theme: PropTypes.object,
  viewingFrom: PropTypes.oneOf(Object.values(enums.CurrentTargetAudience)),
  history: PropTypes.object,
}

AuctionApplicationShell.defaultProps = {
  auth: {},
  overridableComponents: {},
}

export default AuctionApplicationShell
